import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, ago } from '../state.tsx';
import { Loading, useLoad, PlayerLink, Panel, ItemIcon, ItemName } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';

function PersonRow({ p, children }: { p: any; children?: any }) {
  return (
    <div className="card" style={{ alignItems: 'center' }}>
      <Portrait seed={p.portrait} playerId={p.playerId ?? p.id} size={36} />
      <div className="card-body">
        <div className="name"><PlayerLink id={p.playerId ?? p.id} name={p.name} /></div>
        <div className="small muted">Level {p.level}{p.createdAt ? ` · ${ago(p.createdAt)}` : ''}</div>
      </div>
      {children}
    </div>
  );
}

/** Daily gifts: one free gift per member every 24 hours. */
function GiftsPanel({ reloadKey }: { reloadKey: unknown }) {
  const { act, me } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/gifts'), [reloadKey, me?.giftsReady]);
  const [giftId, setGiftId] = useState('mystery_bag');
  if (!data) return null;
  const ready = data.members.filter((m: any) => m.readyAt === 0);
  const gift = data.catalog.find((g: any) => g.id === giftId);
  const hoursLeft = (t: number) => Math.max(1, Math.ceil((t - Date.now()) / 3_600_000));
  const send = async (path: string, body: any, text: (r: any) => string) => {
    const r = await act(path, body, { success: (x) => ({ kind: 'good', text: text(x) }) });
    if (r) reload();
  };
  return (
    <Panel title={<>Daily Gifts <span className="small muted">· free, one per member every 24 hours</span></>}>
      {data.members.length === 0 ? <div className="muted">Gifts are for family. Recruit some mafia members first.</div> : (
        <>
          <div className="row" style={{ alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
            <span>Gift:</span>
            <select value={giftId} onChange={(e) => setGiftId(e.target.value)}>
              {data.catalog.map((g: any) => <option key={g.id} value={g.id} disabled={g.locked}>{g.name}{g.locked ? ` (level ${g.reqLevel})` : ''}</option>)}
            </select>
            {gift?.itemId !== 'mystery' && gift && <ItemIcon itemId={gift.itemId} size={28} />}
            <span className="small muted grow">{gift?.description}</span>
            <button className={`btn green ${ready.length ? '' : 'disabled'}`} onClick={() => send('/api/gifts/send-all', { giftId }, (r) => `Sent ${r.sent} gift(s) to your mafia.`)}>
              Send to everyone ({ready.length})
            </button>
          </div>
          <div className="cards">
            {data.members.map((m: any) => (
              <div key={m.id} className="card" style={{ alignItems: 'center' }}>
                <Portrait seed={m.portrait} playerId={m.id} size={28} />
                <div className="card-body"><div className="name"><PlayerLink id={m.id} name={m.name} /></div></div>
                {m.readyAt === 0 ? <a className="small" onClick={() => send('/api/gifts/send', { recipientId: m.id, giftId }, (r) => `Gift sent to ${r.recipientName}.`)}>Send</a>
                  : <span className="small dim">in {hoursLeft(m.readyAt)}h</span>}
              </div>
            ))}
          </div>
        </>
      )}
      {data.received.length > 0 && (
        <div className="small muted" style={{ marginTop: 6 }}>
          Recently received: {data.received.slice(0, 5).map((g: any, i: number) => <span key={g.id}>{i > 0 && ', '}<ItemName itemId={g.itemId} /> from <PlayerLink id={g.senderId} name={g.senderName} /></span>)}
        </div>
      )}
    </Panel>
  );
}

export function MafiaPage() {
  const { act, me, content } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/mafia'), [me?.pendingInvites]);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<any[] | null>(null);
  if (!data) return <Loading />;
  const doInvite = async (id: number, name: string) => {
    const r = await act('/api/mafia/invite', { playerId: id }, { success: (x) => ({ kind: 'good', text: x.joined ? `${name} is now in your mafia!` : `Invitation sent to ${name}.` }) });
    if (r) reload();
  };
  return (
    <div>
      <h1 className="page-title">My Mafia</h1>
      <p className="muted">
        Your mafia size is <b className="gold" style={{ fontSize: 14 }}>{data.size}</b> (you, {data.members.length} member(s){data.hiredGuns ? ` and ${data.hiredGuns} hired guns` : ''}).
        Bigger mafias fight harder and unlock tougher jobs. Membership works both ways: when someone joins you, you join them.
      </p>
      <div className={`cap-bar ${data.memberCount >= data.cap ? 'full' : ''}`}>
        Members: <b>{data.memberCount} / {data.cap}</b>
        <span className="progress" style={{ width: 160, display: 'inline-block', margin: '0 8px', verticalAlign: 'middle' }}><div style={{ width: `${Math.min(100, (data.memberCount / Math.max(1, data.cap)) * 100)}%` }} /></span>
        {data.memberCount >= data.cap ? <span className="bad">Your mafia is full.</span> : <span className="muted">{data.cap - data.memberCount} slot(s) open.</span>}{' '}
        <a href={`#/profile/${me.id}`}>Raise your limit</a> <span className="small dim">({content.rules.mafiaSlotCost} skill points per extra member; hired guns don’t count)</span>
      </div>
      {data.incoming.length > 0 && (
        <Panel title={`Invitations (${data.incoming.length})`}>
          <table className="list">
            <tbody>
              {data.incoming.map((i: any) => (
                <tr key={i.id}>
                  <td style={{ width: 40 }}><Portrait seed={i.portrait} playerId={i.playerId} size={32} /></td>
                  <td><PlayerLink id={i.playerId} name={i.name} /> <span className="small dim">Level {i.level} · invited you {ago(i.createdAt)}</span></td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    <button className={`btn small green ${data.memberCount >= data.cap ? 'disabled' : ''}`} title={data.memberCount >= data.cap ? 'Your mafia is full' : ''} onClick={async () => { await act('/api/mafia/respond', { inviteId: i.id, accept: true }, { success: () => ({ kind: 'good', text: `${i.name} joined your mafia.` }) }); reload(); }}>Accept</button>{' '}
                    <button className="btn small gray" onClick={async () => { await act('/api/mafia/respond', { inviteId: i.id, accept: false }); reload(); }}>Ignore</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
      <GiftsPanel reloadKey={data.members.length} />
      <div className="row">
        <div className="grow">
          <Panel title={`Members (${data.members.length})`}>
            {data.members.length === 0 ? <div className="muted">No members yet. Invite some people!</div> : (
              <div className="cards">
                {data.members.map((m: any) => (
                  <PersonRow key={m.id} p={m}>
                    <a className="small dim" title="Remove from mafia" onClick={async () => { if (confirm(`Remove ${m.name} from your mafia?`)) { await act('/api/mafia/remove', { playerId: m.id }); reload(); } }}>✕</a>
                  </PersonRow>
                ))}
              </div>
            )}
          </Panel>
        </div>
        <div style={{ width: 260 }}>
          <Panel title="Recruit">
            <form onSubmit={async (e) => { e.preventDefault(); const r = await api.get(`/api/players/search?q=${encodeURIComponent(q)}`); setResults(r.players); }}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name" style={{ width: '70%' }} /> <button className="btn small">Find</button>
            </form>
            {results && (results.length === 0 ? <div className="muted small">No one by that name.</div> : results.filter((p) => p.id !== me.id).map((p) => (
              <div key={p.id} className="feed-item"><div className="grow"><PlayerLink id={p.id} name={p.name} /> <span className="dim small">L{p.level}</span></div><a className="small" onClick={() => doInvite(p.id, p.name)}>Invite</a></div>
            )))}
            <h2>Suggested</h2>
            {data.suggestions.map((p: any) => (
              <div key={p.id} className="feed-item"><div className="grow"><PlayerLink id={p.id} name={p.name} /> <span className="dim small">L{p.level}</span></div><a className="small" onClick={() => doInvite(p.id, p.name)}>Invite</a></div>
            ))}
          </Panel>
          {data.outgoing.length > 0 && (
            <Panel title={`Waiting for answer (${data.outgoing.length})`}>
              {data.outgoing.map((i: any) => (
                <div key={i.id} className="feed-item"><div className="grow"><PlayerLink id={i.playerId} name={i.name} /></div><a className="small dim" onClick={async () => { await act('/api/mafia/cancel', { inviteId: i.id }); reload(); }}>cancel</a></div>
              ))}
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}
